import React, { useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Sword, Brain, Shield, Eye, ExternalLink, Github } from 'lucide-react';
import { projects } from '../data/mock';
import { SECTIONS, createSectionVariants } from '../lib/motionDirector';

const categoryIcons = {
  'AI/ML': Brain,
  'Security': Shield,
  'Computer Vision': Eye,
  'Full-Stack': Sword,
};

const categoryKanji = {
  'AI/ML': '知',
  'Security': '守',
  'Computer Vision': '眨',
  'Full-Stack': '創',
};

const Projects = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [hoveredId, setHoveredId] = useState(null);

  // Parallax scroll effect for kunai
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const kunaiY = useTransform(scrollYProgress, [0, 1], [200, -200]);
  const kunaiRotate = useTransform(scrollYProgress, [0, 1], [-35, -55]);
  const kunaiScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1.1, 0.95]);

  // Motion Director orchestrates project reveals
  const variants = createSectionVariants(SECTIONS.projects);

  return (
    <section id="projects" className="relative py-32 bg-zinc-900 overflow-hidden" ref={ref}>
      {/* Purple-crimson edge glow for variety */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-950/12 via-transparent to-red-950/12" />
      <div className="absolute inset-0 bg-gradient-to-b from-violet-950/8 to-transparent" />
      
      {/* Kunai diagonal backdrop - right side with parallax */}
      <motion.img 
        src="/kunai.png" 
        alt="" 
        className="absolute right-0 top-2/3 -translate-y-1/2 w-[500px] opacity-35 pointer-events-none select-none drop-shadow-[0_0_40px_rgba(139,0,0,0.7)] filter brightness-125 blur-[0.5px]"
        style={{
          y: kunaiY,
          rotate: kunaiRotate,
          scale: kunaiScale,
        }}
      />
      
      {/* Kanji Typography Layer - Large watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-[25rem] font-bold text-red-900/[0.04] select-none tracking-wider">任務</div>
      </div>

      {/* Japanese background elements */}
      <div className="absolute inset-0">
        <div className="absolute bottom-20 left-10 text-[20rem] font-bold text-amber-100/[0.02] select-none">任</div>
        <div className="absolute top-20 right-20 text-[10rem] font-bold text-red-900/[0.03] select-none">務</div>
        
        {/* Scattered cherry blossom petals */}
        <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <radialGradient id="petalGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FFB7C5" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#8B0000" stopOpacity="0.3" />
            </radialGradient>
          </defs>
          {/* Falling petals at various positions */}
          <ellipse cx="150" cy="200" rx="8" ry="12" fill="url(#petalGradient)" transform="rotate(25 150 200)" />
          <ellipse cx="350" cy="450" rx="6" ry="10" fill="url(#petalGradient)" transform="rotate(-15 350 450)" />
          <ellipse cx="900" cy="150" rx="7" ry="11" fill="url(#petalGradient)" transform="rotate(45 900 150)" />
          <ellipse cx="1050" cy="380" rx="5" ry="9" fill="url(#petalGradient)" transform="rotate(-30 1050 380)" />
          <ellipse cx="750" cy="600" rx="8" ry="12" fill="url(#petalGradient)" transform="rotate(60 750 600)" />
          <ellipse cx="200" cy="650" rx="6" ry="10" fill="url(#petalGradient)" transform="rotate(10 200 650)" />
          <ellipse cx="550" cy="100" rx="7" ry="11" fill="url(#petalGradient)" transform="rotate(-45 550 100)" />
          <ellipse cx="1100" cy="550" rx="5" ry="8" fill="url(#petalGradient)" transform="rotate(35 1100 550)" />
        </svg>
      </div>

      <motion.div 
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
        variants={variants.container}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Section Header */}
        <motion.div
          variants={variants.item}
          className="text-center mb-20 relative"
        >
          {/* Circular crimson glow backdrop - like degree token style */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] pointer-events-none" 
               style={{ background: 'radial-gradient(circle at 50% 50%, rgba(220,38,38,0.25) 0%, rgba(139,0,0,0.15) 30%, rgba(139,0,0,0.05) 50%, transparent 70%)' }} 
          />
          
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-red-700" />
              <span className="text-4xl text-red-800/30 drop-shadow-[0_0_30px_rgba(220,38,38,0.4)]">任</span>
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-red-700" />
            </div>
            <span className="inline-block px-4 py-1 text-xs tracking-[0.3em] text-amber-500/80 border border-amber-900/40 rounded-full mb-4 drop-shadow-[0_0_15px_rgba(220,38,38,0.5)]">
              LEGENDARY QUESTS
            </span>
            <h2 className="font-bebas text-5xl md:text-7xl tracking-wider text-amber-50 drop-shadow-[0_0_30px_rgba(220,38,38,0.6)] [text-shadow:0_0_40px_rgba(185,28,28,0.4),0_0_80px_rgba(139,0,0,0.3)]">
              MISSIONS ACCOMPLISHED
            </h2>
          </div>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => {
            const CategoryIcon = categoryIcons[project.category] || Sword;
            const kanji = categoryKanji[project.category] || '任';
            const isHovered = hoveredId === project.id;

            return (
              <motion.div
                key={project.id}
                variants={variants.item}
                onMouseEnter={() => setHoveredId(project.id)}
                onMouseLeave={() => setHoveredId(null)}
                className="group relative"
              >
                <motion.div 
                  className="relative p-6 bg-black/50 border border-amber-900/20 rounded-lg h-full overflow-hidden"
                  whileHover={{ 
                    borderColor: 'rgba(139, 0, 0, 0.6)',
                    boxShadow: '0 25px 50px -12px rgba(139, 0, 0, 0.5), 0 0 0 1px rgba(251, 191, 36, 0.1)'
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Background kanji */}
                  <motion.span 
                    className="absolute -right-6 -bottom-6 text-9xl font-bold text-amber-100/[0.02] transition-all duration-500"
                    animate={{ opacity: isHovered ? 0.08 : 0.02, scale: isHovered ? 1.1 : 1 }}
                  >
                    {kanji}
                  </motion.span>

                  {/* Category Badge */}
                  <motion.div 
                    className="relative flex items-center gap-2 mb-4"
                    whileHover={{ x: 3 }}
                    transition={{ duration: 0.2 }}
                  >
                    <motion.div 
                      className="p-1.5 bg-red-900/20 rounded border border-red-900/30"
                      whileHover={{ rotate: 10 }}
                    >
                      <CategoryIcon className="w-4 h-4 text-red-500" />
                    </motion.div>
                    <span className="text-xs tracking-wider text-amber-500/80 uppercase">
                      {project.category}
                    </span>
                  </motion.div>

                  {/* Title with kanji */}
                  <motion.h3 
                    className="font-bebas text-2xl tracking-wider text-amber-50 mb-2 relative z-10"
                    whileHover={{ x: 2, y: -1 }}
                    transition={{ duration: 0.15 }}
                  >
                    {project.title}
                  </motion.h3>

                  {/* Subtitle */}
                  <p className="relative text-sm text-red-400/70 italic mb-4">
                    "{project.subtitle}"
                  </p>

                  {/* Description */}
                  <p className="relative text-sm text-amber-100/50 mb-6 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Tech Stack */}
                  <div className="relative flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-1 text-[10px] tracking-wider text-amber-100/60 bg-zinc-900/80 border border-amber-900/20 rounded"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* GitHub Link */}
                  {project.github && (
                    <motion.a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative inline-flex items-center gap-2 text-sm text-amber-400/80 hover:text-amber-300 transition-colors group/link"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Github className="w-4 h-4" />
                      <span className="tracking-wide">View Project</span>
                      <ExternalLink className="w-3 h-3 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                    </motion.a>
                  )}

                  {/* Bottom accent */}
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-700 via-amber-600 to-red-700 origin-left"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: isHovered ? 1 : 0 }}
                    transition={{ duration: 0.4 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};

export default Projects;
